import selectAnnotationDictationPlaybackPath from './selectAnnotationDictationPlaybackPath';

interface IData {
    dictationFilePaths?: string[],
    transcripts?: unknown[],
}

/**
 * Builds a revision key for the browser-playable annotation dictation asset.
 *
 * Annotation edits can regenerate dictation audio by overwriting the same MP3
 * path in Storage. In that flow, `dictationFilePaths.length` does not change,
 * so the UI needs a richer signal than "path count increased" to know when it
 * should fetch the refreshed asset again.
 *
 * The playback path stays in the key so path changes still invalidate the
 * cached audio. The latest transcript snapshot is included so same-path
 * regenerations also invalidate the key once the backend has produced the new
 * dictation/transcript payload.
 */
function buildAnnotationDictationReadyKey({
    dictationFilePaths,
    transcripts,
}: IData): string | null {
    const playbackSelection = selectAnnotationDictationPlaybackPath({
        dictationFilePaths,
    });

    if (!playbackSelection.playbackPath && !playbackSelection.shouldPollForMp3) {
        return null;
    }

    const latestTranscript = Array.isArray(transcripts) && transcripts.length > 0
        ? transcripts[0]
        : null;

    let transcriptSignature = 'no-transcript';
    if (latestTranscript != null) {
        try {
            transcriptSignature = JSON.stringify(latestTranscript);
        } catch (error) {
            transcriptSignature = 'transcript-present';
        }
    }

    if (playbackSelection.playbackPath) {
        return `${playbackSelection.playbackPath}::${transcriptSignature}`;
    }

    return `pending-mp3::${(dictationFilePaths || []).join('|')}::${transcriptSignature}`;
}

export default buildAnnotationDictationReadyKey;
